import React, { forwardRef } from "react";
import QRCode from "react-qr-code";
import moment from "moment";

export interface ProductLabel {
  itemCode: string;
  itemName: string;
  price: number;
  quantity: number;
  batchCode?: string;
  status?: "pending" | "loading" | "success" | "error";
  errorMsg?: string;
}

export interface PrintConfig {
  marginLeft: number | string;
  marginTop: number | string;
  columnGap: number | string;
  rowGap: number | string;
  pageWidth?: number | string;
  pageHeight?: number | string;
}

interface LabelTemplateProps {
  labels: ProductLabel[];
  printConfig: PrintConfig;
}

export const LabelTemplate = forwardRef<HTMLDivElement, LabelTemplateProps>(
  ({ labels, printConfig }, ref) => {
    const { marginLeft, marginTop, columnGap, rowGap, pageWidth, pageHeight } = printConfig;
    const rowsPerPage = 6;
    const itemsPerPage = 5 * rowsPerPage;
    const pWidth = pageWidth ? `${pageWidth}mm` : "210mm";
    const pHeight = pageHeight ? `${pageHeight}mm` : "165mm";
    
    // Auto center if margins are not provided
    const justify = marginLeft === "" ? "center" : "start";
    const align = marginTop === "" ? "center" : "start";
    const pLeft = marginLeft !== "" && Number(marginLeft) >= 0 ? `${marginLeft}mm` : "0";
    const mLeft = marginLeft !== "" && Number(marginLeft) < 0 ? `${marginLeft}mm` : "0";
    const pTop = marginTop !== "" && Number(marginTop) >= 0 ? `${marginTop}mm` : "0";
    const mTop = marginTop !== "" && Number(marginTop) < 0 ? `${marginTop}mm` : "0";
    const cGap = columnGap === "" ? "2mm" : `${columnGap}mm`;
    const rGap = rowGap === "" ? "2mm" : `${rowGap}mm`;

    return (
      <div style={{ display: "none" }}>
        <div ref={ref}>
          <style type="text/css" media="print">
            {`
              @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;900&display=swap');

              @page {
                size: ${pWidth} ${pHeight};
                margin: 0;
              }
              body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              .page-container {
                width: ${pWidth};
                height: ${pHeight};
                box-sizing: border-box;
                padding-left: ${pLeft};
                margin-left: ${mLeft};
                padding-top: ${pTop};
                margin-top: ${mTop};
                display: grid;
                grid-template-columns: repeat(5, 36mm);
                grid-template-rows: repeat(${rowsPerPage}, 25mm);
                column-gap: ${cGap}; 
                row-gap: ${rGap};
                justify-content: ${justify};
                align-content: ${align};
                page-break-after: always;
              }
              .label-container {
                width: 36mm;
                height: 25mm;
                box-sizing: border-box;
                padding: 0.8mm 1.2mm;
                display: flex;
                flex-direction: column;
                background-color: white;
                font-family: 'Montserrat', sans-serif;
                overflow: hidden;
              }
              .label-top {
                display: flex;
                gap: 1.5mm;
                height: 12mm;
                overflow: hidden;
              }
              .qr-box {
                width: 7.5mm;
                height: 7.5mm;
                flex-shrink: 0;
                align-self: flex-start;
                margin-top: 0.5mm;
              }
              .product-name {
                font-size: 5.5pt;
                font-weight: 600;
                line-height: 1.1;
                max-height: 9mm;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 6;
                -webkit-box-orient: vertical;
                margin: 0;
                flex-grow: 1;
                word-break: break-word;
              }
              .label-footer {
                flex-shrink: 0;
              }
              .divider {
                border-top: 0.5px solid black;
                margin: 0.3mm 0 0.2mm 0;
              }
              .price-box {
                text-align: right;
                font-size: 8.5pt;
                font-weight: 900;
                line-height: 1;
              }
              .label-bottom {
                display: flex;
                justify-content: space-between;
                font-size: 4.5pt;
                margin-top: 0.5mm;
                line-height: 1;
              }
              .batch-code {
                font-weight: 700;
              }
            `}
          </style>
          {(() => {
            const validLabels = labels.filter((l) => l.status === "success" && l.quantity > 0);
            const flatLabels: ProductLabel[] = [];
            validLabels.forEach((item) => {
              for (let i = 0; i < item.quantity; i++) {
                flatLabels.push(item);
              }
            });

            const pages = [];
            for (let i = 0; i < flatLabels.length; i += itemsPerPage) {
              pages.push(flatLabels.slice(i, i + itemsPerPage));
            }

            return pages.map((page, pageIndex) => (
              <div key={pageIndex} className="page-container">
                {page.map((item, index) => (
                  <div key={index} className="label-container">
                    <div className="label-top">
                      <div className="qr-box">
                        <QRCode
                          value={item.itemCode || "NO_CODE"}
                          size={100}
                          style={{ width: "100%", height: "100%" }}
                          viewBox={"0 0 100 100"}
                        />
                      </div>
                      <div className="product-name">
                        {item.itemName || "Tên sản phẩm"}
                      </div>
                    </div>
                    <div className="label-footer">
                      <div className="divider"></div>
                      <div className="price-box">
                        {new Intl.NumberFormat("vi-VN").format(item.price || 0)} đ
                      </div>
                      <div className="label-bottom">
                        <div>{moment().format("DD/MM/YYYY")}</div>
                        <div className="batch-code">
                          {item.batchCode || item.itemCode}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ));
          })()}
        </div>
      </div>
    );
  }
);
